import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cn } from "~/utils/helpers"
import { DotsThree } from "@phosphor-icons/react"
import { appTitleName } from "~/utils/environment"
import { useEffect } from "react"
import { isEqual, truncate } from "lodash"
import { z } from "zod"
import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"
import { Link, useLocation } from "@tanstack/react-router"
import useTitle from "~/hooks/useTitle"
import { useSidebarItems } from "~/hooks/useSidebarItems"
import { CRUMB_SEPARATOR, usePublishNavContext } from "~/stores/nav-context-store"

const Breadcrumb = React.forwardRef<
  HTMLElement,
  React.ComponentPropsWithoutRef<"nav"> & {
    separator?: React.ReactNode
  }
>(({ ...props }, ref) => <nav ref={ref} aria-label="breadcrumb" {...props} />)
Breadcrumb.displayName = "Breadcrumb"

const BreadcrumbList = React.forwardRef<HTMLOListElement, React.ComponentPropsWithoutRef<"ol">>(
  ({ className, ...props }, ref) => (
    <ol
      ref={ref}
      className={cn(
        "flex flex-wrap items-center gap-1.5 break-words label-base text-fg-subtle",
        className,
      )}
      {...props}
    />
  ),
)
BreadcrumbList.displayName = "BreadcrumbList"

const BreadcrumbItem = React.forwardRef<HTMLLIElement, React.ComponentPropsWithoutRef<"li">>(
  ({ className, ...props }, ref) => (
    <li ref={ref} className={cn("inline-flex items-center gap-1.5", className)} {...props} />
  ),
)
BreadcrumbItem.displayName = "BreadcrumbItem"

const BreadcrumbLink = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<"a"> & {
    asChild?: boolean
  }
>(({ asChild, className, ...props }, ref) => {
  const Comp = asChild ? Slot : "a"

  return (
    <Comp
      ref={ref}
      className={cn("transition-colors hover:text-foreground", className)}
      {...props}
    />
  )
})
BreadcrumbLink.displayName = "BreadcrumbLink"

const BreadcrumbPage = React.forwardRef<HTMLSpanElement, React.ComponentPropsWithoutRef<"span">>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn("label-base text-fg-base", className)}
      {...props}
    />
  ),
)
BreadcrumbPage.displayName = "BreadcrumbPage"

const BreadcrumbSeparator = ({ className, ...props }: React.ComponentProps<"li">) => (
  <li
    role="presentation"
    aria-hidden="true"
    className={cn("[&>svg]:w-3.5 [&>svg]:h-3.5 text-fg-muted label-base", className)}
    {...props}
  >
    /
  </li>
)
BreadcrumbSeparator.displayName = "BreadcrumbSeparator"

const BreadcrumbEllipsis = ({ className, ...props }: React.ComponentProps<"span">) => (
  <span
    role="presentation"
    aria-hidden="true"
    className={cn("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <DotsThree className="h-4 w-4" />
    <span className="sr-only">More</span>
  </span>
)
BreadcrumbEllipsis.displayName = "BreadcrumbElipssis"

// Module-level: stable identities keep the storage hooks' validate effects from re-running on
// every render.
const HOME_PAGE = { path: "/", title: "Home" }
const lastNonVariantPathSchema = z.object({ path: z.string(), title: z.string() })

export const useLastNonVariantPath = () => {
  return useSafeLocalStorage("lastNonVariantPath", HOME_PAGE, lastNonVariantPathSchema, true)
}

// One URL search param as TanStack Router parses it: `?view=40` → 40, `?isGroup=false` → false.
const searchParamValueSchema = z.union([z.string(), z.number(), z.boolean(), z.array(z.string())])

// A page's search params keyed by name, e.g. `{ view: 40, isGroup: false }`.
const lastNonVariantSearchSchema = z.record(z.string(), searchParamValueSchema)

type SearchParams = z.infer<typeof lastNonVariantSearchSchema>

const NO_SEARCH: SearchParams = {}

export const useLastNonVariantSearch = () => {
  return useSafeLocalStorage("lastNonVariantSearch", NO_SEARCH, lastNonVariantSearchSchema, true)
}

// Auth and product pages are never a back-link target: the link is shown on the product page.
const isBackLinkTarget = (pathname: string) =>
  !pathname.includes("/auth") && !pathname.includes("/product")

/**
 * Records the page the product page's back link returns to: its path and breadcrumb title, and
 * its current search params (e.g. the active `?view=`). The search is saved on every change, not
 * only on arrival, so the link returns to the state last shown rather than the landing one.
 * Each value is written only when it differs from what is stored.
 */
function useRecordBackLinkTarget(title: string | undefined) {
  const { pathname, search } = useLocation()
  const [lastNonVariantPath, setLastNonVariantPath] = useLastNonVariantPath()
  const [lastNonVariantSearch, setLastNonVariantSearch] = useLastNonVariantSearch()

  useEffect(() => {
    if (!pathname || !title || !isBackLinkTarget(pathname)) return

    const page = { path: pathname, title }
    if (!isEqual(page, lastNonVariantPath)) setLastNonVariantPath(page)

    // A search the schema can't store is saved as `{}`, what storage validation would reset it to.
    const pageSearch = lastNonVariantSearchSchema.safeParse(search).success ? search : {}
    if (!isEqual(pageSearch, lastNonVariantSearch)) setLastNonVariantSearch(pageSearch)
  }, [pathname, search, title])
}

interface BreadcrumbItem {
  label: string
  href?: string
  onClick?: () => void
  search?: SearchParams
}

export function useAutoBreadcrumbs(): BreadcrumbItem[] | null {
  const { topSidebarItems, bottomSidebarItems } = useSidebarItems()
  const { pathname } = useLocation()
  const allItems = [...topSidebarItems, ...bottomSidebarItems]

  for (const parent of allItems) {
    for (const sub of parent.subItems ?? []) {
      if (pathname === sub.pathName || pathname.startsWith(sub.pathName + "/")) {
        return [{ label: parent.name, href: parent.pathName }, { label: sub.name }]
      }
    }
  }

  for (const parent of allItems) {
    if (pathname === parent.pathName || pathname.startsWith(parent.pathName + "/")) {
      return [{ label: parent.name }]
    }
  }

  return null
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[]
}

const BreadcrumbWrapper: React.FC<BreadcrumbsProps> = ({ items: manualItems }) => {
  const autoItems = useAutoBreadcrumbs()
  const items = manualItems ?? autoItems ?? []

  const lastLabel = items.at(-1)?.label
  useTitle(lastLabel ? `${appTitleName()} - ${lastLabel}` : appTitleName())
  usePublishNavContext("breadcrumb", items.map((item) => item.label).join(CRUMB_SEPARATOR))

  useRecordBackLinkTarget(lastLabel)

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {items.map((item, index) => {
          const isLast = index === items.length - 1

          return (
            <React.Fragment key={item.href ?? item.label}>
              {index > 0 && <BreadcrumbSeparator />}
              <BreadcrumbItem>
                {isLast ? (
                  <BreadcrumbPage>{item.label}</BreadcrumbPage>
                ) : item.onClick ? (
                  <BreadcrumbLink className="cursor-pointer" onClick={item.onClick}>
                    {item.label.length > 30 ? truncate(item.label, { length: 30 }) : item.label}
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={item.href ?? "/"} search={item.search}>
                      {item.label.length > 30 ? truncate(item.label, { length: 30 }) : item.label}
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </React.Fragment>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
  BreadcrumbWrapper,
}
